import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Stack,
  Theme,
  Tooltip,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { Shipments } from "../../../../../models/Shipments";
import { ShipmentStatusEnum } from "../../../../../models/Shipments/ShipmentStatus";
import { formatName } from "../../../../../views/components/common/functions/formatString";
import {
  formatDate,
  formatRelativeDate,
} from "../../../../../views/components/common/functions/formatDate";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";
import LabelSecondary from "./LabelSecondary";

interface ShipmentListCardProps {
  shipment: Shipments;
  isSelected: boolean;
  canView: boolean;
  canEdit: boolean;
  canCancel: boolean;
  showMoveToNew: boolean;
  showAddItem: boolean;
  onToggleSelect: () => void;
  onView: () => void;
  onEdit: () => void;
  onAddItem?: () => void;
  onMarkReturned: () => void;
  onMoveToNew: () => void;
  onActivityHistory?: () => void;
  onDelete?: () => void;
}

const getCardDivider = (theme: Theme) =>
  `1px solid ${theme.palette.customColors.lightGray}`;

const getCardBackgroundColor = (shipment: Shipments, theme: Theme) => {
  const today = dayjs().startOf("day");
  const { orange, red, blue, green } = theme.palette;

  if (
    shipment.status === ShipmentStatusEnum.READY_TO_SHIP &&
    shipment.status_change_date
  ) {
    const statusChangeDaysAgo = today.diff(
      dayjs(shipment.status_change_date).startOf("day"),
      "day",
    );

    if (statusChangeDaysAgo === 1) return `${orange.main}10`;
    if (statusChangeDaysAgo > 1) return `${red.main}10`;
    return `${blue.main}10`;
  }

  if (shipment.status === ShipmentStatusEnum.NEW && shipment.created_date) {
    const createdDaysAgo = today.diff(
      dayjs(shipment.created_date).startOf("day"),
      "day",
    );

    if (createdDaysAgo === 1) return `${orange.main}10`;
    if (createdDaysAgo > 1) return `${red.main}10`;
    return `${green.main}10`;
  }

  return theme.palette.customColors.white;
};

const statusChipSx = (theme: Theme, status?: number) => {
  const c = theme.palette.customColors;
  const { orange, purple, goldenYellow, blushPink } = theme.palette;

  const infoTone = {
    color: c.darkBlue,
    bg: c.blueLightBackground,
    border: c.softBorder,
  };
  const warningTone = {
    color: orange.dark,
    bg: c.lightOrange,
    border: orange.light,
  };
  const successTone = {
    color: c.successGreen,
    bg: c.successGreenLightBackground,
    border: c.successGreenLightBorder,
  };
  const returnTone = {
    color: orange.dark,
    bg: c.lightYellow,
    border: goldenYellow.main,
  };
  const dangerTone = {
    color: c.errorRed,
    bg: blushPink.main,
    border: c.borderErrorRedColor,
  };
  const purpleTone = {
    color: c.purpleMain,
    bg: c.lightPurple,
    border: purple.light,
  };

  const map: Record<number, { color: string; bg: string; border: string }> = {
    [ShipmentStatusEnum.NEW]: infoTone,
    [ShipmentStatusEnum.READY_TO_SHIP]: warningTone,
    [ShipmentStatusEnum.SHIPPED]: infoTone,
    [ShipmentStatusEnum.OUT_FOR_DELIVERY]: purpleTone,
    [ShipmentStatusEnum.DELIVERED]: successTone,
    [ShipmentStatusEnum.RETURN_REQUESTED]: returnTone,
    [ShipmentStatusEnum.RETURNED]: returnTone,
    [ShipmentStatusEnum.CANCELLED]: dangerTone,
    [ShipmentStatusEnum.UNDELIVERED]: dangerTone,
    [ShipmentStatusEnum.DAMAGED]: dangerTone,
  };

  const tone = (status && map[status]) || {
    color: c.grayText,
    bg: c.veryLightGray,
    border: c.lightGray,
  };

  return {
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 700,
    borderRadius: "999px",
    color: tone.color,
    backgroundColor: tone.bg,
    border: `1px solid ${tone.border}`,
    "& .MuiChip-label": { px: 1.1 },
  };
};

const typeChipSx = (theme: Theme) => {
  const c = theme.palette.customColors;
  return {
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 600,
    borderRadius: "999px",
    color: c.grayText,
    backgroundColor: c.white,
    border: `1px solid ${c.switchTrackGray}`,
    "& .MuiChip-label": { px: 1.1 },
  };
};

const duplicateChipSx = (theme: Theme) => {
  const c = theme.palette.customColors;
  const { orange } = theme.palette;
  return {
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 700,
    borderRadius: "999px",
    color: orange.dark,
    backgroundColor: c.lightOrange,
    border: `1px solid ${orange.light}`,
    "& .MuiChip-label": { px: 1.1 },
  };
};

const bookingChipSx = (theme: Theme, bookingState?: string | null) => {
  const c = theme.palette.customColors;
  const { orange, blushPink, purple } = theme.palette;
  const state = String(bookingState || "").toUpperCase();

  const tones: Record<string, { color: string; bg: string; border: string }> = {
    BOOKED: {
      color: c.successGreen,
      bg: c.successGreenLightBackground,
      border: c.successGreenLightBorder,
    },
    FAILED: {
      color: c.errorRed,
      bg: blushPink.main,
      border: c.borderErrorRedColor,
    },
    QUEUED: {
      color: orange.dark,
      bg: c.lightOrange,
      border: orange.light,
    },
    IN_PROGRESS: {
      color: c.purpleMain,
      bg: c.lightPurple,
      border: purple.light,
    },
    CANCELLED: {
      color: c.grayText,
      bg: c.veryLightGray,
      border: c.lightGray,
    },
  };

  const tone = tones[state] || {
    color: c.grayText,
    bg: c.white,
    border: c.switchTrackGray,
  };

  return {
    height: 24,
    fontSize: "0.72rem",
    fontWeight: 700,
    borderRadius: "999px",
    color: tone.color,
    backgroundColor: tone.bg,
    border: `1px solid ${tone.border}`,
    "& .MuiChip-label": { px: 1.1 },
  };
};

const getRecipientName = (shipment: Shipments) => {
  const firstName = formatName(shipment.first_name);
  const lastName = formatName(shipment.last_name);
  return `${firstName}${lastName ? ` ${lastName}` : ""}`.trim() || "-";
};

const getLocation = (shipment: Shipments) => {
  return [shipment.city, shipment.state].filter(Boolean).join(", ") || "-";
};

const getDimensionsLabel = () => "See details";

const getNote = (shipment: Shipments) => shipment.order_notes?.trim() || null;

const getPacksLabel = (shipment: Shipments) => {
  const raw = shipment.packs_label?.trim();
  if (!raw || raw === "-" || raw === "--") return null;

  const formatted = raw
    .split("%")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const slash = part.lastIndexOf("/");
      if (slash <= 0) return part;
      const name = part.slice(0, slash).trim();
      const qty = part.slice(slash + 1).trim();
      return qty ? `${name} × ${qty}` : name;
    })
    .join(" • ");

  return formatted || null;
};

const formatCost = (value?: number | string | null) => {
  if (value === undefined || value === null || value === "") return "-";
  const amount = Number(value);
  if (Number.isNaN(amount)) return `${value} INR`;
  return `${amount} INR`;
};

const titleCase = (value?: string | null) => {
  if (!value) return "N/A";
  return value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatBookingStateLabel = (bookingState?: string | null) => {
  if (!bookingState?.trim()) return "Not booked";
  return titleCase(bookingState.replace(/_/g, " "));
};

const formatPlacedOn = (dateString?: string) => {
  const formatted = formatDate(dateString);
  if (!dateString || formatted === "-") return "-";
  const relative = formatRelativeDate(dateString);
  return relative && relative !== "-" ? `${formatted} ${relative}` : formatted;
};

const BookingStateChip: React.FC<{ shipment: Shipments }> = ({ shipment }) => {
  const theme = useTheme<Theme>();
  const chip = (
    <Chip
      label={formatBookingStateLabel(shipment.booking_state)}
      size="small"
      sx={bookingChipSx(theme, shipment.booking_state)}
    />
  );
  const failedError =
    String(shipment.booking_state || "").toUpperCase() === "FAILED"
      ? shipment.last_booking_error?.trim()
      : "";

  if (!failedError) {
    return chip;
  }

  return <Tooltip title={failedError}>{chip}</Tooltip>;
};

const AwbSecondary: React.FC<{ shipment: Shipments }> = ({ shipment }) => {
  const theme = useTheme<Theme>();
  const colors = theme.palette.customColors;
  const [copied, setCopied] = useState(false);
  const awb = shipment.awb?.trim() || "";
  const trackingUrl = shipment.awb_track_url?.trim() || "";
  const displayAwb = awb ? (awb.startsWith("#") ? awb : `#${awb}`) : "-";

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!awb) return;
    try {
      await navigator.clipboard.writeText(awb);
      setCopied(true);
    } catch {
      // Clipboard may be blocked; keep the AWB visible either way.
    }
  };

  const awbValue =
    trackingUrl && awb ? (
      <Box
        component="a"
        href={trackingUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        sx={{
          color: colors.darkBlue,
          textDecoration: "underline",
          fontWeight: 500,
        }}
      >
        {displayAwb}
      </Box>
    ) : (
      <Box component="span">{displayAwb}</Box>
    );

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        maxWidth: "100%",
        fontSize: "0.75rem",
      }}
    >
      {`Inv : #${shipment.inventory_id || "-"} | AWB : `}
      {awbValue}
      {awb ? (
        <Tooltip title={copied ? "Copied" : "Copy AWB"}>
          <IconButton
            size="small"
            onClick={(event) => {
              void handleCopy(event);
            }}
            aria-label={`Copy AWB ${awb}`}
            sx={{
              p: 0.3,
              flexShrink: 0,
              color: copied ? colors.purpleMain : colors.greyTextColor,
            }}
          >
            {copied ? (
              <CheckIcon sx={{ fontSize: 12 }} />
            ) : (
              <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
            )}
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

const InfoColumn: React.FC<{
  label: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  showDivider?: boolean;
}> = ({ label, primary, secondary, showDivider = true }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const divider = getCardDivider(theme);

  return (
    <Box
      sx={{
        minWidth: 0,
        px: { xs: 0, md: 2 },
        py: { xs: 1, md: 0.5 },
        position: "relative",
        borderBottom: { xs: divider, md: "none" },
        "&::after": {
          content: '""',
          display: {
            xs: "none",
            md: showDivider ? "block" : "none",
          },
          position: "absolute",
          right: 0,
          top: 8,
          bottom: 8,
          width: "1px",
          backgroundColor: theme.palette.customColors.lightGray,
        },
      }}
    >
      <CustomTypography
        variant="caption"
        color={colors.subtitle}
        fontWeight={500}
        sx={{ display: "block", mb: 0.35, fontSize: "0.72rem" }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="body2"
        fontWeight={700}
        color={colors.title}
        title={typeof primary === "string" ? primary : undefined}
        sx={{
          display: "block",
          fontSize: "0.88rem",
          lineHeight: 1.3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {primary}
      </CustomTypography>
      {secondary ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 0.2,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {typeof secondary === "string" ? (
            <CustomTypography
              variant="caption"
              color={colors.subtitle}
              fontWeight={500}
              sx={{
                display: "block",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {secondary}
            </CustomTypography>
          ) : (
            secondary
          )}
        </Box>
      ) : null}
    </Box>
  );
};

const ShipmentListCard: React.FC<ShipmentListCardProps> = ({
  shipment,
  isSelected,
  canView,
  canEdit,
  canCancel,
  showMoveToNew,
  showAddItem,
  onToggleSelect,
  onView,
  onEdit,
  onAddItem,
  onMarkReturned,
  onMoveToNew,
  onActivityHistory,
  onDelete,
}) => {
  const theme = useTheme<Theme>();
  const colors = theme.palette.customColors;
  const pageColors = getModernPageColors(theme);
  const divider = getCardDivider(theme);
  const note = getNote(shipment);
  const packsLabel = getPacksLabel(shipment);
  const productsCount =
    shipment.products_count == null ? null : Number(shipment.products_count);

  const buttonBaseSx = {
    minWidth: "fit-content",
    minHeight: 32,
    height: 32,
    px: 1.75,
    fontSize: "0.75rem",
    fontWeight: 700,
    borderRadius: "8px",
    boxShadow: "none",
  };

  const viewEditButtonSx = {
    ...buttonBaseSx,
    color: colors.purpleMain,
    backgroundColor: colors.lightPurple,
    "&:hover": {
      backgroundColor: theme.palette.lavenderBlue.main,
      boxShadow: "none",
    },
  };

  const addItemButtonSx = {
    ...buttonBaseSx,
    color: colors.pinkDark,
    backgroundColor: colors.pinkLight,
    "&:hover": {
      backgroundColor: theme.palette.blushPink.main,
      boxShadow: "none",
    },
  };

  const returnedButtonSx = {
    ...buttonBaseSx,
    color: colors.lightred,
    backgroundColor: theme.palette.blushPink.light,
    "&:hover": {
      backgroundColor: theme.palette.blushPink.main,
      boxShadow: "none",
    },
  };

  return (
    <Box
      onClick={() => {
        if (canView) onView();
      }}
      sx={{
        borderRadius: "12px",
        border: isSelected ? `1px solid ${colors.purpleMain}` : divider,
        backgroundColor: getCardBackgroundColor(shipment, theme),
        overflow: "hidden",
        cursor: canView ? "pointer" : "default",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          borderBottom: divider,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Checkbox
            color="primary"
            size="small"
            checked={isSelected}
            onClick={(event) => {
              event.stopPropagation();
              onToggleSelect();
            }}
            sx={{ p: 0.25 }}
          />
          <CustomTypography
            variant="heading7"
            fontWeight={800}
            color={pageColors.title}
            sx={{ fontSize: "1rem", lineHeight: 1.2 }}
          >
            #{shipment.id}
          </CustomTypography>
          <Chip
            label={titleCase(shipment.status_label)}
            size="small"
            sx={statusChipSx(theme, shipment.status)}
          />
          {shipment.shipment_type_label ? (
            <Chip
              label={titleCase(shipment.shipment_type_label)}
              size="small"
              sx={typeChipSx(theme)}
            />
          ) : null}
          {shipment.is_duplicate === true || shipment.is_duplicate === 1 ? (
            <Chip label="Duplicate" size="small" sx={duplicateChipSx(theme)} />
          ) : null}
          <BookingStateChip shipment={shipment} />
        </Stack>
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          sx={{ ml: "auto", flexShrink: 0 }}
        >
          {onActivityHistory ? (
            <Tooltip title="Activity History">
              <IconButton
                size="small"
                aria-label="Shipment activity history"
                onClick={(event) => {
                  event.stopPropagation();
                  onActivityHistory();
                }}
                sx={{
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main + "14",
                  },
                }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {canCancel &&
          onDelete &&
          shipment.status !== ShipmentStatusEnum.CANCELLED ? (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                aria-label="Delete shipment"
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete();
                }}
                sx={{
                  color: colors.errorRed,
                  "&:hover": {
                    backgroundColor: theme.palette.blushPink.light,
                  },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1.3fr 1.2fr 1.25fr 1.25fr 0.85fr",
          },
          px: { xs: 1.5, md: 0.75 },
          py: { xs: 0.5, md: 1.25 },
          alignItems: "stretch",
        }}
      >
        <InfoColumn
          label="Recipient"
          primary={getRecipientName(shipment)}
          secondary={shipment.email || "-"}
        />
        <InfoColumn label="Customer" primary={shipment.customer_name || "-"} />
        <InfoColumn
          label="Order ID / NV / AWB"
          primary={`Order #${shipment.order_id || "-"}`}
          secondary={<AwbSecondary shipment={shipment} />}
        />
        <InfoColumn
          label="Location & Specs"
          primary={getLocation(shipment)}
          secondary={`Dims/Wt : ${getDimensionsLabel()}`}
        />
        <InfoColumn
          label="Carrier Details"
          primary={`${shipment.delivery_partner_name || "-"}`}
          secondary={
            <LabelSecondary
              labelS3Key={shipment.label_s3_key}
              prefix={`DV : ${shipment.declared_value ?? "-"} | Label : `}
            />
          }
          showDivider={false}
        />
        <InfoColumn
          label="MPS Shipping label"
          primary={<LabelSecondary labelS3Key={shipment.mps_label_s3_key} />}
        />
        {shipment.products_count != null && Number.isFinite(productsCount) && (
          <InfoColumn
            label="Added Products"
            primary={(shipment.products_count ?? 0).toString()}
          />
        )}
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderTop: divider,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            minWidth: 0,
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
              flex: "0 1 auto",
            }}
          >
            {note ? (
              <Box
                sx={{
                  px: 1.25,
                  py: 0.55,
                  borderRadius: "999px",
                  backgroundColor: colors.lightPurple,
                  width: "fit-content",
                  maxWidth: { xs: "min(224px, 100%)", sm: 264, md: 296 },
                  minWidth: 0,
                  flex: "0 1 auto",
                  overflow: "hidden",
                }}
              >
                <CustomTypography
                  variant="caption"
                  color={pageColors.title}
                  fontWeight={600}
                  sx={{
                    fontSize: "0.75rem",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={note}
                >
                  Note - {note}
                </CustomTypography>
              </Box>
            ) : null}

            {packsLabel ? (
              <Box
                sx={{
                  px: 1.25,
                  py: 0.55,
                  borderRadius: "999px",
                  backgroundColor: colors.blueLightBackground,
                  border: `1px solid ${colors.softBorder}`,
                  width: "fit-content",
                  maxWidth: { xs: "min(224px, 100%)", sm: 264, md: 296 },
                  minWidth: 0,
                  flex: "0 1 auto",
                  overflow: "hidden",
                }}
              >
                <CustomTypography
                  variant="caption"
                  color={colors.darkBlue}
                  fontWeight={600}
                  sx={{
                    fontSize: "0.75rem",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={packsLabel}
                >
                  Packs - {packsLabel}
                </CustomTypography>
              </Box>
            ) : null}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            sx={{ flexShrink: 0 }}
          >
            {canView ? (
              <AppButton
                label="View"
                onClick={onView}
                customStyles={viewEditButtonSx}
              />
            ) : null}
            {canEdit ? (
              <AppButton
                label="Edit"
                onClick={onEdit}
                customStyles={viewEditButtonSx}
              />
            ) : null}
            {showAddItem && onAddItem ? (
              <AppButton
                label="Add Item"
                onClick={onAddItem}
                customStyles={addItemButtonSx}
              />
            ) : null}
            {canEdit && showMoveToNew ? (
              <AppButton
                label="Move to New"
                onClick={onMoveToNew}
                customStyles={viewEditButtonSx}
              />
            ) : null}
            {canCancel ? (
              <AppButton
                label="Mark Returned"
                onClick={onMarkReturned}
                customStyles={returnedButtonSx}
              />
            ) : null}
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          sx={{ mt: 1 }}
        >
          <CustomTypography
            variant="caption"
            color={pageColors.subtitle}
            fontWeight={600}
            sx={{ fontSize: "0.72rem" }}
          >
            Placed By {shipment.placed_by_admin_name || "-"} on{" "}
            {formatPlacedOn(shipment.created_date)}
          </CustomTypography>
          <CustomTypography
            variant="caption"
            color={pageColors.subtitle}
            fontWeight={600}
            sx={{ fontSize: "0.72rem" }}
          >
            |
          </CustomTypography>
          <CustomTypography
            variant="caption"
            color={pageColors.subtitle}
            fontWeight={600}
            sx={{ fontSize: "0.72rem" }}
          >
            Updated By {shipment.modified_by_name || "-"} on{" "}
            {formatDate(shipment.modified_date)}
          </CustomTypography>
        </Stack>
      </Box>
    </Box>
  );
};

export default ShipmentListCard;
